import axios from 'axios';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('at1s_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('at1s_user');
      localStorage.removeItem('at1s_token');
      if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
        window.location.href = '/login';
      }
    }
    // Normalize Pydantic 422 validation errors: detail may be an array of objects
    if (error.response?.data?.detail && Array.isArray(error.response.data.detail)) {
      error.response.data.detail = error.response.data.detail
        .map(e => e.msg || JSON.stringify(e))
        .join('; ');
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },
  register: async (email, password, name, company, phone, address) => {
    const response = await api.post('/auth/register', { email, password, name, company, phone, address });
    return response.data;
  },
  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
};

// Products APIs
export const productsAPI = {
  getAll: async (thickness) => {
    const params = thickness ? `?thickness=${thickness}` : '';
    const response = await api.get(`/products${params}`);
    return response.data;
  },
  getSizes: async () => {
    const response = await api.get('/products/sizes');
    return response.data;
  },
  getPricing: async (size, merv) => {
    const response = await api.get(`/products/pricing?size=${size}&merv=${merv}`);
    return response.data;
  },
};

// Orders APIs
export const ordersAPI = {
  getAll: async (customerId = null) => {
    const url = customerId ? `/orders?customer_id=${customerId}` : '/orders';
    const response = await api.get(url);
    return response.data;
  },
  getById: async (orderId) => {
    const response = await api.get(`/orders/${orderId}`);
    return response.data;
  },
  create: async (orderData) => {
    const response = await api.post('/orders', orderData);
    return response.data;
  },
  updateDraft: async (orderId, orderData) => {
    const response = await api.put(`/orders/${orderId}/update-draft`, orderData);
    return response.data;
  },
  updateStatus: async (orderId, status, trackingNumber = null) => {
    const response = await api.patch(`/orders/${orderId}/status`, { status, tracking_number: trackingNumber });
    return response.data;
  },
  getDeliveries: async (orderId) => {
    const response = await api.get(`/orders/${orderId}/deliveries`);
    return response.data;
  },
  createDelivery: async (orderId, data) => {
    const response = await api.post(`/orders/${orderId}/deliveries`, data);
    return response.data;
  },
  updateDeliveryStatus: async (orderId, deliveryId, status) => {
    const response = await api.patch(`/orders/${orderId}/deliveries/${deliveryId}/status`, { status });
    return response.data;
  },
  updateCarrierTracking: async (orderId, deliveryId, carrierTrackingNumber) => {
    const response = await api.patch(`/orders/${orderId}/deliveries/${deliveryId}/tracking`, { carrier_tracking_number: carrierTrackingNumber });
    return response.data;
  },
  updateInvoiceStatus: async (orderId, invoiceStatus) => {
    const response = await api.patch(`/orders/${orderId}/invoice-status`, { invoice_status: invoiceStatus });
    return response.data;
  },
  cancelOrder: async (orderId, reason) => {
    const response = await api.post(`/orders/${orderId}/cancel`, { reason });
    return response.data;
  },
  customerCancelOrder: async (orderId, reason) => {
    const response = await api.post(`/orders/${orderId}/customer-cancel`, { reason });
    return response.data;
  },
  softDeleteOrder: async (orderId, reason) => {
    const response = await api.post(`/orders/${orderId}/soft-delete`, { reason });
    return response.data;
  },
  restoreOrder: async (orderId) => {
    const response = await api.post(`/orders/${orderId}/restore`);
    return response.data;
  },
  downloadInvoice: async (orderId) => {
    const response = await api.get(`/orders/${orderId}/invoice`, { responseType: 'blob' });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoice-${orderId}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => window.URL.revokeObjectURL(url), 5000);
  },
  viewInvoice: async (orderId) => {
    const response = await api.get(`/orders/${orderId}/invoice`, { responseType: 'blob' });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  },
};

// Tracking API (public)
export const trackingAPI = {
  track: async (trackingNumber) => {
    const response = await api.get(`/tracking/${trackingNumber}`);
    return response.data;
  },
};

// Payments API
export const paymentsAPI = {
  getConfig: async () => {
    const response = await api.get('/payments/config');
    return response.data;
  },
  createIntent: async (orderId) => {
    const response = await api.post('/payments/create-intent', { order_id: orderId });
    return response.data;
  },
  confirmPayment: async (orderId, paymentIntentId) => {
    const response = await api.post('/payments/confirm', { order_id: orderId, payment_intent_id: paymentIntentId });
    return response.data;
  },
  getRefundLog: async () => {
    const response = await api.get('/payments/refund-log');
    return response.data;
  },
  createSetupIntent: async (subscriptionId) => {
    const response = await api.post('/payments/setup-intent', { subscription_id: subscriptionId });
    return response.data;
  },
  confirmSetup: async (subscriptionId, setupIntentId) => {
    const response = await api.post('/payments/confirm-setup', { subscription_id: subscriptionId, setup_intent_id: setupIntentId });
    return response.data;
  },
  getPaymentMethod: async (subscriptionId) => {
    const response = await api.get(`/payments/payment-method/${subscriptionId}`);
    return response.data;
  },
};

// Inventory APIs
export const inventoryAPI = {
  getAll: async () => {
    const response = await api.get('/inventory');
    return response.data;
  },
  update: async (itemId, updateData) => {
    const response = await api.patch(`/inventory/${itemId}`, updateData);
    return response.data;
  },
  createAdjustment: async (adjustments) => {
    const response = await api.post('/inventory/adjustments', { adjustments });
    return response.data;
  },
  writeOff: async (data) => {
    const response = await api.post('/inventory/write-off', data);
    return response.data;
  },
  forceDelete: async (data) => {
    const response = await api.delete('/inventory/force-delete', { data });
    return response.data;
  },
  updateReorderPoint: async (data) => {
    const response = await api.put('/inventory/reorder-point', data);
    return response.data;
  },
  downloadTemplate: async () => {
    const response = await api.get('/inventory/template/download', { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url; a.download = 'inventory_template.csv';
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  },
  uploadInventory: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/inventory/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

// Purchase Orders APIs
export const purchaseOrdersAPI = {
  getAll: async () => {
    const response = await api.get('/purchase-orders');
    return response.data;
  },
  getById: async (poId) => {
    const response = await api.get(`/purchase-orders/${poId}`);
    return response.data;
  },
  create: async (poData) => {
    const response = await api.post('/purchase-orders', poData);
    return response.data;
  },
  updateStatus: async (poId, status, trackingNumber = null) => {
    const response = await api.patch(`/purchase-orders/${poId}/status`, { status, tracking_number: trackingNumber });
    return response.data;
  },
  receiveGoods: async (poId, items, notes = '') => {
    const response = await api.post(`/purchase-orders/${poId}/receive`, { items, notes });
    return response.data;
  },
  uploadAttachment: async (poId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/purchase-orders/${poId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  downloadAttachmentUrl: (poId, attachmentId) => {
    return `${API_BASE}/purchase-orders/${poId}/attachments/${attachmentId}/download`;
  },
  deleteAttachment: async (poId, attachmentId) => {
    const response = await api.delete(`/purchase-orders/${poId}/attachments/${attachmentId}`);
    return response.data;
  },
  forceDelete: async (poId) => {
    const response = await api.delete(`/purchase-orders/${poId}`);
    return response.data;
  },
  purgeAll: async () => {
    const response = await api.delete('/purchase-orders');
    return response.data;
  },
};

// Financial APIs
export const financialAPI = {
  getAccounts: async () => {
    const response = await api.get('/financial/accounts');
    return response.data;
  },
  createAccount: async (accountData) => {
    const response = await api.post('/financial/accounts', accountData);
    return response.data;
  },
  getJournalEntries: async () => {
    const response = await api.get('/financial/journal-entries');
    return response.data;
  },
  createJournalEntry: async (entryData) => {
    const response = await api.post('/financial/journal-entries', entryData);
    return response.data;
  },
  getBalanceSheet: async () => {
    const response = await api.get('/financial/balance-sheet');
    return response.data;
  },
  getIncomeStatement: async () => {
    const response = await api.get('/financial/income-statement');
    return response.data;
  },
  updateBankBalance: async (accountId, bankBalance, bankNotes) => {
    const response = await api.post(`/financial/accounts/${accountId}/bank-balance`, { bank_balance: bankBalance, bank_notes: bankNotes });
    return response.data;
  },
};

// Subscriptions APIs
export const subscriptionsAPI = {
  getAll: async () => {
    const response = await api.get('/subscriptions');
    return response.data;
  },
  getById: async (subId) => {
    const response = await api.get(`/subscriptions/${subId}`);
    return response.data;
  },
  cancel: async (subId) => {
    const response = await api.patch(`/subscriptions/${subId}/cancel`);
    return response.data;
  },
  reactivate: async (subId) => {
    const response = await api.patch(`/subscriptions/${subId}/reactivate`);
    return response.data;
  },
  pause: async (subId) => {
    const response = await api.patch(`/subscriptions/${subId}/pause`);
    return response.data;
  },
  resume: async (subId) => {
    const response = await api.patch(`/subscriptions/${subId}/resume`);
    return response.data;
  },
  skip: async (subId) => {
    const response = await api.patch(`/subscriptions/${subId}/skip`);
    return response.data;
  },
  updateItems: async (subId, items) => {
    const response = await api.patch(`/subscriptions/${subId}/items`, { items });
    return response.data;
  },
  updateAddress: async (subId, address) => {
    const response = await api.patch(`/subscriptions/${subId}/address`, { shipping_address: address });
    return response.data;
  },
  processDue: async () => {
    const response = await api.post('/subscriptions/admin/process-due');
    return response.data;
  },
};

// Master Data APIs
export const masterDataAPI = {
  getProducts: async () => {
    const response = await api.get('/master-data/products');
    return response.data;
  },
  getProductHistory: async (productId) => {
    const response = await api.get(`/master-data/products/${productId}`);
    return response.data;
  },
  createProduct: async (data) => {
    const response = await api.post('/master-data/products', data);
    return response.data;
  },
  updateProduct: async (productId, data) => {
    const response = await api.patch(`/master-data/products/${productId}`, data);
    return response.data;
  },
  deleteProduct: async (productId) => {
    const response = await api.delete(`/master-data/products/${productId}`);
    return response.data;
  },
  restoreProduct: async (productId) => {
    const response = await api.patch(`/master-data/products/${productId}/restore`);
    return response.data;
  },
  selectPrice: async (productId, recordId) => {
    const response = await api.patch(`/master-data/products/${productId}/select-price/${recordId}`);
    return response.data;
  },
  downloadTemplate: () => {
    return `${API_BASE}/master-data/products/template/download`;
  },
  downloadTemplateBlob: async () => {
    // Direct navigation — browser handles the download natively via Content-Disposition header
    window.location.href = `${API_BASE}/master-data/products/template/download`;
    return { saved: true, name: 'product_master_template.xlsx' };
  },
  uploadProducts: async (file, token) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/master-data/products/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

// Customer Master APIs
export const customerMasterAPI = {
  getAll: async (includeDeleted = false) => {
    const response = await api.get(`/master-data/customers?include_deleted=${includeDeleted}`);
    return response.data;
  },
  getById: async (customerId) => {
    const response = await api.get(`/master-data/customers/${customerId}`);
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/master-data/customers', data);
    return response.data;
  },
  update: async (customerId, data) => {
    const response = await api.patch(`/master-data/customers/${customerId}`, data);
    return response.data;
  },
  delete: async (customerId) => {
    const response = await api.delete(`/master-data/customers/${customerId}`);
    return response.data;
  },
  restore: async (customerId) => {
    const response = await api.patch(`/master-data/customers/${customerId}/restore`);
    return response.data;
  },
  downloadTemplate: () => {
    return `${API_BASE}/master-data/customers/template/download`;
  },
  downloadTemplateBlob: async () => {
    const response = await api.get('/master-data/customers/template/download', { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url; a.download = 'customer_master_template.xlsx';
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  },
  uploadCustomers: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/master-data/customers/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

// Warehouse Master APIs
export const warehouseAPI = {
  getAll: async (includeInactive = false) => {
    const response = await api.get(`/master-data/warehouses?include_inactive=${includeInactive}`);
    return response.data;
  },
  getById: async (warehouseId) => {
    const response = await api.get(`/master-data/warehouses/${warehouseId}`);
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/master-data/warehouses', data);
    return response.data;
  },
  update: async (warehouseId, data) => {
    const response = await api.patch(`/master-data/warehouses/${warehouseId}`, data);
    return response.data;
  },
  delete: async (warehouseId) => {
    const response = await api.delete(`/master-data/warehouses/${warehouseId}`);
    return response.data;
  },
  restore: async (warehouseId) => {
    const response = await api.patch(`/master-data/warehouses/${warehouseId}/restore`);
    return response.data;
  },
};

// Stock Transfer APIs
export const stockTransferAPI = {
  getAll: async () => {
    const response = await api.get('/stock-transfers');
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/stock-transfers', data);
    return response.data;
  },
};

// Reports APIs
export const reportsAPI = {
  salesSummary: async (startDate, endDate) => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    const response = await api.get(`/reports/sales-summary?${params}`);
    return response.data;
  },
  invoices: async (status) => {
    const params = status ? `?status=${status}` : '';
    const response = await api.get(`/reports/invoices${params}`);
    return response.data;
  },
  purchaseOrders: async (status) => {
    const params = status ? `?status=${status}` : '';
    const response = await api.get(`/reports/purchase-orders${params}`);
    return response.data;
  },
  inventory: async () => {
    const response = await api.get('/reports/inventory');
    return response.data;
  },
  exportExcel: async (type, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    const url = `/reports/${type}/export${qs ? '?' + qs : ''}`;
    const response = await api.get(url, { responseType: 'blob' });
    const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `${type}_report.xlsx`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(link.href);
  },
};

export const promoCodesAPI = {
  getAll: async () => { const r = await api.get('/promo-codes'); return r.data; },
  create: async (data) => { const r = await api.post('/promo-codes', data); return r.data; },
  update: async (id, data) => { const r = await api.put(`/promo-codes/${id}`, data); return r.data; },
  delete: async (id) => { const r = await api.delete(`/promo-codes/${id}`); return r.data; },
  validate: async (code, subtotal) => { const r = await api.post('/promo-codes/validate', { code, subtotal }); return r.data; },
  getUsageReport: async () => { const r = await api.get('/promo-codes/usage-report'); return r.data; },
};


export const priceListsAPI = {
  getAll: async () => { const r = await api.get('/price-lists'); return r.data; },
  create: async (data) => { const r = await api.post('/price-lists', data); return r.data; },
  update: async (id, data) => { const r = await api.put(`/price-lists/${id}`, data); return r.data; },
  delete: async (id) => { const r = await api.delete(`/price-lists/${id}`); return r.data; },
  getProducts: async () => { const r = await api.get('/price-lists/products/available'); return r.data; },
  getPdf: async (id) => {
    const r = await api.get(`/price-lists/${id}/pdf`, { responseType: 'blob' });
    const blob = new Blob([r.data], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `price-list-${id}.pdf`; a.click();
    URL.revokeObjectURL(url);
  },
};

export const quotesAPI = {
  getAll: async () => { const r = await api.get('/quotes'); return r.data; },
  create: async (data) => { const r = await api.post('/quotes', data); return r.data; },
  update: async (id, data) => { const r = await api.put(`/quotes/${id}`, data); return r.data; },
  delete: async (id) => { const r = await api.delete(`/quotes/${id}`); return r.data; },
  convert: async (id) => { const r = await api.post(`/quotes/${id}/convert`); return r.data; },
  getCustomers: async () => { const r = await api.get('/quotes/customers'); return r.data; },
  getProducts: async () => { const r = await api.get('/quotes/products'); return r.data; },
};

export const leadsAPI = {
  getAll: async () => { const r = await api.get('/leads'); return r.data; },
  create: async (data) => { const r = await api.post('/leads', data); return r.data; },
  get: async (id) => { const r = await api.get(`/leads/${id}`); return r.data; },
  update: async (id, data) => { const r = await api.put(`/leads/${id}`, data); return r.data; },
  delete: async (id) => { const r = await api.delete(`/leads/${id}`); return r.data; },
  addNote: async (id, text) => { const r = await api.post(`/leads/${id}/notes`, { text }); return r.data; },
  convert: async (id) => { const r = await api.post(`/leads/${id}/convert`); return r.data; },
};

export default api;
